/**
 * Type guard that checks whether a value is neither `undefined` nor `null`.
 * This utility is used to ensure that a given value is defined before performing operations on it.
 * It helps narrow down types in TypeScript, allowing the compiler to treat the value as fully defined (`T`).
 * This is especially useful in optional chaining, conditional rendering, or runtime validations.
 *
 * @param val The value to check.
 * @returns `true` if the value is not `undefined` and not `null`, otherwise `false`.
 *
 * @example
 * ```ts
 * if (isDefined(value)) {
 *   // value is guaranteed to be of type T
 *   use(value);
 * }
 * ```
 */
export const isDefined = <T>(val: T | null | undefined): val is T => val !== undefined && val !== null;

/**
 * Type guard that checks whether a value is a fully populated object rather than just a string reference.
 * This utility is typically used in Payload CMS where relationship fields can either return a string ID (unpopulated)
 * or a fully expanded object (populated). It helps distinguish between these cases at runtime, allowing safe access
 * to nested properties when the value is an object.
 *
 * @param val The value to check, which may be either a string or a populated object.
 * @returns `true` if the value is not a string (i.e. It's an object), otherwise `false`.
 *
 * @example
 * ```ts
 * if (isPopulated(relation.value)) {
 *   relation.value.slug; // safe to access
 * }
 * ```
 */
export const isPopulated = <T>(val: T | string): val is T => typeof val !== 'string';

/**
 * Type guard that checks whether a value is both defined and fully populated (not a string reference).
 * This utility combines the checks of `isDefined` and `isPopulated` to ensure that a value is neither `null` nor `undefined`,
 * and that it is a populated object rather than a string identifier. It is especially useful in CMS environments like Payload CMS,
 * where relationship fields may return either an ID (as a string) or a populated object depending on the query context.
 *
 * @param val The value to check, which may be `null`, `undefined`, a string ID, or a populated object.
 * @returns `true` if the value is defined and is not a string (i.e., it is a fully populated object).
 *
 * @example
 * ```ts
 * if (isDefinedAndPopulated(link.internalLink?.value)) {
 *   link.internalLink.value.slug; // Safe access
 * }
 * ```
 */
// eslint-disable-next-line @stylistic/max-len
export const isDefinedAndPopulated = <T>(val: T | string | null | undefined): val is T => isDefined(val) && isPopulated(val);